// A small body's outer radius by direction, read off its triangle mesh: for
// every texel of an equirectangular grid (planetocentric longitude east from
// u = 0, latitude north at the top row) the farthest point where the ray
// from the body's centre meets the surface. The shading uses it to march
// sunlight across the body for cast shadows and to find hollows for the
// ambient light (src/smallbodies/material.js); scripts/build-small-bodies.mjs
// uses the same grid to line each map up with its shape.
//
// Positions are in the model's own frame, Y up (glTF): body north is +Y and
// the prime meridian +X, so a body-fixed (x, y, z) is stored as (x, z, -y)
// and longitude is atan2(-Z, X). Triangles are rasterised in longitude and
// latitude, so the cost is the texels they cover, not texels times triangles.

const TWO_PI = Math.PI * 2;

export function directionLonLat(x, y, z) {
  const r = Math.hypot(x, y, z) || 1;
  let lon = Math.atan2(-z, x);
  if (lon < 0) lon += TWO_PI;
  return [lon, Math.asin(Math.max(-1, Math.min(1, y / r)))];
}

// Ray from the origin along d against triangle (a, b, c): the distance, or -1.
function rayTriangle(dx, dy, dz, ax, ay, az, bx, by, bz, cx, cy, cz) {
  const e1x = bx - ax, e1y = by - ay, e1z = bz - az;
  const e2x = cx - ax, e2y = cy - ay, e2z = cz - az;
  const px = dy * e2z - dz * e2y, py = dz * e2x - dx * e2z, pz = dx * e2y - dy * e2x;
  const det = e1x * px + e1y * py + e1z * pz;
  if (Math.abs(det) < 1e-14) return -1;
  const inv = 1 / det;
  const tx = -ax, ty = -ay, tz = -az;
  const u = (tx * px + ty * py + tz * pz) * inv;
  if (u < -1e-6 || u > 1 + 1e-6) return -1;
  const qx = ty * e1z - tz * e1y, qy = tz * e1x - tx * e1z, qz = tx * e1y - ty * e1x;
  const v = (dx * qx + dy * qy + dz * qz) * inv;
  if (v < -1e-6 || u + v > 1 + 1e-6) return -1;
  const t = (e2x * qx + e2y * qy + e2z * qz) * inv;
  return t > 0 ? t : -1;
}

// Returns a Float32Array of width * height radii (0 where no ray hit, which
// fillRadiusHoles then closes).
export function rasterizeRadiusMap(positions, indices, width, height) {
  const out = new Float32Array(width * height);
  const triCount = indices.length / 3;
  const lon = new Float64Array(3), lat = new Float64Array(3);
  const dirs = new Float64Array(width * height * 3);
  for (let j = 0; j < height; j++) {
    const la = Math.PI / 2 - ((j + 0.5) / height) * Math.PI;
    const cl = Math.cos(la), sl = Math.sin(la);
    for (let i = 0; i < width; i++) {
      const lo = ((i + 0.5) / width) * TWO_PI;
      const k = (j * width + i) * 3;
      dirs[k] = cl * Math.cos(lo);
      dirs[k + 1] = sl;
      dirs[k + 2] = -cl * Math.sin(lo);
    }
  }
  for (let t = 0; t < triCount; t++) {
    const ia = indices[t * 3] * 3, ib = indices[t * 3 + 1] * 3, ic = indices[t * 3 + 2] * 3;
    const ax = positions[ia], ay = positions[ia + 1], az = positions[ia + 2];
    const bx = positions[ib], by = positions[ib + 1], bz = positions[ib + 2];
    const cx = positions[ic], cy = positions[ic + 1], cz = positions[ic + 2];
    const verts = [[ax, ay, az], [bx, by, bz], [cx, cy, cz]];
    for (let v = 0; v < 3; v++) {
      const [lo, la] = directionLonLat(verts[v][0], verts[v][1], verts[v][2]);
      lon[v] = lo; lat[v] = la;
    }
    let latMin = Math.min(lat[0], lat[1], lat[2]);
    let latMax = Math.max(lat[0], lat[1], lat[2]);
    // A triangle round a pole spans every longitude; one across the seam is
    // unwrapped so its longitudes are contiguous.
    let lonMin = Math.min(lon[0], lon[1], lon[2]);
    let lonMax = Math.max(lon[0], lon[1], lon[2]);
    let span = lonMax - lonMin;
    if (span > Math.PI) {
      for (let v = 0; v < 3; v++) if (lon[v] < Math.PI) lon[v] += TWO_PI;
      lonMin = Math.min(lon[0], lon[1], lon[2]);
      lonMax = Math.max(lon[0], lon[1], lon[2]);
      span = lonMax - lonMin;
    }
    // Does the triangle contain a pole? Then it covers all longitudes there.
    const poleN = rayTriangle(0, 1, 0, ax, ay, az, bx, by, bz, cx, cy, cz) > 0;
    const poleS = rayTriangle(0, -1, 0, ax, ay, az, bx, by, bz, cx, cy, cz) > 0;
    if (poleN) { latMax = Math.PI / 2; lonMin = 0; lonMax = TWO_PI; }
    if (poleS) { latMin = -Math.PI / 2; lonMin = 0; lonMax = TWO_PI; }
    // A texel's direction can meet a triangle whose corners all lie a hair
    // off its row or column, so the box grows by a texel each way.
    const j0 = Math.max(0, Math.floor((Math.PI / 2 - latMax) / Math.PI * height - 1));
    const j1 = Math.min(height - 1, Math.ceil((Math.PI / 2 - latMin) / Math.PI * height + 1));
    const i0 = Math.floor(lonMin / TWO_PI * width - 1);
    const i1 = Math.ceil(lonMax / TWO_PI * width + 1);
    for (let j = j0; j <= j1; j++) {
      for (let ii = i0; ii <= i1; ii++) {
        const i = ((ii % width) + width) % width;
        const p = j * width + i;
        const k = p * 3;
        const d = rayTriangle(dirs[k], dirs[k + 1], dirs[k + 2], ax, ay, az, bx, by, bz, cx, cy, cz);
        if (d > out[p]) out[p] = d;
      }
    }
  }
  return out;
}

// Texels no ray reached (none should, on a closed mesh) take the mean of
// their filled neighbours, repeated until none are left.
export function fillRadiusHoles(radii, width, height) {
  let holes = 0;
  for (let p = 0; p < radii.length; p++) if (!(radii[p] > 0)) holes++;
  let guard = 64;
  while (holes > 0 && guard-- > 0) {
    const next = radii.slice();
    holes = 0;
    for (let j = 0; j < height; j++) {
      for (let i = 0; i < width; i++) {
        const p = j * width + i;
        if (radii[p] > 0) continue;
        let sum = 0, n = 0;
        for (let dj = -1; dj <= 1; dj++) {
          const jj = j + dj;
          if (jj < 0 || jj >= height) continue;
          for (let di = -1; di <= 1; di++) {
            const q = jj * width + (((i + di) % width) + width) % width;
            if (radii[q] > 0) { sum += radii[q]; n++; }
          }
        }
        if (n) next[p] = sum / n; else holes++;
      }
    }
    radii.set(next);
  }
  return radii;
}
